import React from 'react';
import { DeckSlideWrapper } from '../DeckSlideWrapper';
import { PieChart, Target } from 'lucide-react';

export const Slide11TheAsk: React.FC = () => {
  return (
    <DeckSlideWrapper
      slideNumber={11}
      totalSlides={12}
      category="The Investment Ask"
      title="Raising $1.0M Seed to Capture Enterprise AI Security"
      subtitle="18 months of disciplined runway to achieve $1.2M ARR, SOC 2 certification, and 50+ enterprise deployments."
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-3.5 sm:gap-5 items-stretch">
        {/* Left Raise Summary & Use of Funds */}
        <div className="lg:col-span-7 flex flex-col justify-between p-3.5 sm:p-4.5 rounded-2xl bg-white/90 dark:bg-navy-850/80 border border-slate-200 dark:border-navy-700 shadow-sm space-y-3 sm:space-y-4">
          <div className="flex items-center justify-between border-b border-slate-200 dark:border-navy-700/60 pb-2.5 sm:pb-3">
            <div>
              <span className="text-[11px] font-mono text-slate-500 font-bold uppercase">TARGET CAPITAL RAISE</span>
              <div className="text-2xl sm:text-4xl font-extrabold text-amber-600 dark:text-gold-400 font-mono">
                $1,000,000
              </div>
            </div>
            <div className="text-right">
              <span className="text-[11px] font-mono text-slate-500 font-bold uppercase">RUNWAY</span>
              <div className="text-xl sm:text-3xl font-extrabold text-slate-900 dark:text-white font-mono">
                18 Months
              </div>
            </div>
          </div>

          {/* Allocation Breakdown */}
          <div className="space-y-2 sm:space-y-2.5">
            <h4 className="font-mono text-[11px] sm:text-xs font-bold text-slate-900 dark:text-white uppercase tracking-wider flex items-center gap-1.5">
              <PieChart className="w-3.5 h-3.5 text-gold-500" />
              <span>Capital Allocation</span>
            </h4>

            {/* Bar 1 */}
            <div>
              <div className="flex justify-between text-xs font-mono mb-0.5 sm:mb-1">
                <span className="font-bold text-slate-900 dark:text-white">60% // Core Engineering &amp; R&amp;D</span>
                <span className="text-amber-700 dark:text-gold-400 font-bold">$600k</span>
              </div>
              <div className="w-full h-2 bg-slate-200 dark:bg-navy-900 rounded-full overflow-hidden">
                <div className="h-full bg-gold-500 rounded-full" style={{ width: '60%' }} />
              </div>
              <p className="text-[10px] sm:text-[11px] text-slate-500 mt-0.5">
                DLP Token Vault optimizations, custom Haskell Parsec rules, agentic gateway protocol.
              </p>
            </div>

            {/* Bar 2 */}
            <div>
              <div className="flex justify-between text-xs font-mono mb-0.5 sm:mb-1">
                <span className="font-bold text-slate-900 dark:text-white">25% // Go-To-Market &amp; DevRel</span>
                <span className="text-blue-600 dark:text-blue-400 font-bold">$250k</span>
              </div>
              <div className="w-full h-2 bg-slate-200 dark:bg-navy-900 rounded-full overflow-hidden">
                <div className="h-full bg-blue-500 rounded-full" style={{ width: '25%' }} />
              </div>
              <p className="text-[10px] sm:text-[11px] text-slate-500 mt-0.5">
                Developer community growth, enterprise design partner pilots, and outbound CISO sales.
              </p>
            </div>

            {/* Bar 3 */}
            <div>
              <div className="flex justify-between text-xs font-mono mb-0.5 sm:mb-1">
                <span className="font-bold text-slate-900 dark:text-white">15% // Compliance, Audits &amp; Ops</span>
                <span className="text-purple-600 dark:text-purple-400 font-bold">$150k</span>
              </div>
              <div className="w-full h-2 bg-slate-200 dark:bg-navy-900 rounded-full overflow-hidden">
                <div className="h-full bg-purple-500 rounded-full" style={{ width: '15%' }} />
              </div>
              <p className="text-[10px] sm:text-[11px] text-slate-500 mt-0.5">
                SOC 2 Type I/II audit, ISO 27001 readiness, legal, and high-availability cloud infrastructure.
              </p>
            </div>
          </div>
        </div>

        {/* Right 18-Month Target Outcomes */}
        <div className="lg:col-span-5 flex flex-col justify-between p-3.5 sm:p-4.5 rounded-2xl bg-slate-900 dark:bg-navy-950 text-slate-200 border border-slate-700 dark:border-navy-700 shadow-surface space-y-2.5 sm:space-y-3">
          <div className="flex items-center gap-2 border-b border-slate-800 dark:border-navy-800 pb-2">
            <Target className="w-4 h-4 text-gold-400" />
            <h4 className="font-mono text-xs font-bold text-white uppercase tracking-wider">
              18-Month Success Targets
            </h4>
          </div>

          <div className="space-y-2 sm:space-y-2.5">
            <div className="p-2.5 sm:p-3 rounded-xl bg-slate-950 dark:bg-navy-900 border border-slate-800 dark:border-navy-800">
              <span className="text-xl sm:text-2xl font-bold text-gold-400 font-mono">$1.2M ARR</span>
              <p className="text-[11px] sm:text-xs text-slate-300 mt-0.5">
                Achieved via 30+ Enterprise licenses and 200+ managed cloud teams.
              </p>
            </div>

            <div className="p-2.5 sm:p-3 rounded-xl bg-slate-950 dark:bg-navy-900 border border-slate-800 dark:border-navy-800">
              <span className="text-xl sm:text-2xl font-bold text-emerald-400 font-mono">50+ Enterprise Orgs</span>
              <p className="text-[11px] sm:text-xs text-slate-300 mt-0.5">
                Securing millions of internal enterprise AI transactions daily.
              </p>
            </div>

            <div className="p-2.5 sm:p-3 rounded-xl bg-slate-950 dark:bg-navy-900 border border-slate-800 dark:border-navy-800">
              <span className="text-xl sm:text-2xl font-bold text-blue-400 font-mono">SOC 2 &amp; HIPAA Certified</span>
              <p className="text-[11px] sm:text-xs text-slate-300 mt-0.5">
                Full institutional compliance readiness for Series A acceleration.
              </p>
            </div>
          </div>

          <div className="text-[10px] sm:text-[11px] font-mono text-slate-400 text-center">
            Instrument: Standard Post-Money SAFE / Convertible Note ($8.0M Cap)
          </div>
        </div>
      </div>
    </DeckSlideWrapper>
  );
};
